import React, { useState } from "react";
import clx from "classnames";

import dayjs from "../../../lib/dayjs/dayjs";
import { ActionViewDetails } from "./ActionViewDetails";
import { useToastUpdater } from "../../../context/toastContext/toastContext";
import useAction from "../../../hooks/data/useAction";
import actionsService from "../../../services/actions.service";
import useActionComments from "../../../hooks/data/useActionComments";

import { ActionStatus } from "../../../types/Action";
import useAuthSession from "../../../lib/auth/hooks/useAuthSession";

import ButtonNoStyles from "../../common/Buttons/ButtonNoStyles/ButtonNoStyles";
import Typography from "../../common/Typography/Typography";
import Button from "../../common/Buttons/Button/Button";
import Icon from "../../common/Icon/Icon";
import Tag from "../../common/Tag/Tag";
import Card from "../../common/Card/Card";
import Loading from "../../Loading/Loading";
import ContainerSpinner from "../../common/Spinner/ContainerSpinner/ContainerSpinner";
import Modal from "../../common/Modals/Modal/Modal";
import Alert from "../../Alert/Alert";
import ActionViewCommentsContainer from "./ActionViewCommentsContainer/ActionViewCommentsContainer";

import styles from "./ActionView.module.css";

enum TagColor {
    red = "plum",
    amber = "mustard",
    green = "mint",
    transparent = "transparent",
}

enum StatusColor {
    "In Progress" = "mustard",
    "Closed" = "mocha",
    "Open" = "velvet",
}

interface Props {
    actionId: string;
    onClose?: () => void;
    onActionStatusChange?: (actionId: string, status: ActionStatus) => void;
    onCommented?: (actionId: string) => void;
}

const ActionView = ({ actionId, onClose, onActionStatusChange, onCommented }: Props) => {
    const { session } = useAuthSession({ required: true });
    const [isProcessing, setIsProcessing] = useState(false);
    const [showCommentWarning, setShowCommentWarning] = useState(false);

    const { action, isLoading, error, mutateAction } = useAction(actionId);

    const { comments } = useActionComments(actionId);
    const { addToast } = useToastUpdater();

    const commentAdded = () => {
        if (onCommented) onCommented(actionId);
    };

    // Sets the current action being viewed to closed.
    const closeAction = async () => {
        setIsProcessing(true);
        setShowCommentWarning(false);

        try {
            await actionsService.closeAction(actionId);
            addToast(`Action #${actionId} has successfully been closed`, "success", "");
            if (action) {
                mutateAction({ ...action, Status: ActionStatus.closed });
            }
        } catch (error) {
            addToast(`Action #${actionId} could not be closed. Please try again`, "error", "");
        }

        setIsProcessing(false);

        if (onActionStatusChange) {
            onActionStatusChange(actionId, ActionStatus.closed);
        }
    };

    // Sets the current action being viewed to 'in progress'.
    const progressAction = async () => {
        setIsProcessing(true);

        try {
            await actionsService.progressAction(actionId);
            addToast(`Action #${actionId} is now in progress`, "success", "");
            if (action) {
                mutateAction({ ...action, Status: ActionStatus.inProgress });
            }
            if (onActionStatusChange) onActionStatusChange(actionId, ActionStatus.inProgress);
        } catch (error) {
            addToast(`Action #${actionId} could not be set to in progress. Please try again`, "error", "");
        }

        setIsProcessing(false);
    };

    // Handles the click event for the close action button.
    const handleCloseActionClick = () => {
        // Filter the comments by the current user.
        const currentUserComments = comments?.filter((comment) => session?.user.name === comment.UpdateBy) || [];

        // There must be at least one comment to close an action by the current user.
        if (!currentUserComments.length) {
            return setShowCommentWarning(true);
        }

        // Check if a comment has been added in the last 5 minutes.
        const hasCommentBeenAddedInTheLastFiveMinutes = currentUserComments.some((comment) => {
            const commentDate = dayjs.utc(comment.CreatedAt, "YYYY-MM-DD HH:mm:ss.SSS").tz("Europe/London");
            const fiveMinutesAgo = dayjs().subtract(5, "minutes");

            return commentDate.isAfter(fiveMinutesAgo);
        });

        // Display the warning confirmation modal if there are no comments in the last 5 minutes. Otherwise,
        // close the action.
        return hasCommentBeenAddedInTheLastFiveMinutes ? closeAction() : setShowCommentWarning(true);
    };

    // Handles the click event for the set in progress action button.
    const handleProgressActionClick = () => progressAction();

    const closeModal = () => setShowCommentWarning(false);
    const canCloseActions = session?.user?.canCloseActions === "1";
    const disableActionButtons = !canCloseActions;

    return (
        <Card padding="none" className={clx("rounded drop-shadow-md flex flex-col max-w-7xl", styles.fullScreen)}>
            <Loading isLoading={isLoading} error={error} loadingFallback={<ContainerSpinner />}>
                <div className={clx("grid grid-cols-2 grow-0 shrink-0", styles.card, styles.cardHeader)}>
                    <div className="grid grid-cols-1 gap-1 justify-items-start md:flex md:flex-row md:items-center">
                        <Typography as="h3" variant="subHeading">
                            Action &nbsp; #{actionId}
                        </Typography>
                        <div className="flex gap-0.5 capitalize pt-0.5 md:pt-0">
                            <Tag icon="flag" color={TagColor[action?.Rating || "transparent"]}>
                                {action?.Rating}
                            </Tag>
                            {action?.Status ? <Tag color={StatusColor[action?.Status || "Open"]}>{action.Status}</Tag> : null}
                            <Tag color="mocha">{action?.ActionType}</Tag>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 gap-1 justify-items-end md:flex md:flex-row md:items-center justify-self-end">
                        {(() => {
                            if (action?.Status === ActionStatus.closed) {
                                return null;
                            }

                            if (disableActionButtons) {
                                return (
                                    <div className="order-2 md:order-1">
                                        <Button
                                            title="You do not have access to close actions."
                                            loading={isProcessing}
                                            disabled
                                            variant="primaryDisabled"
                                        >
                                            <span className="hidden sm:inline">mark as closed</span>
                                        </Button>
                                    </div>
                                );
                            }
                            if (!action?.DateCompleted && action?.Status === ActionStatus.inProgress) {
                                return (
                                    <div className="order-2 md:order-1">
                                        <Button
                                            title="Mark as closed"
                                            onClick={handleCloseActionClick}
                                            loading={isProcessing}
                                            disabled={isProcessing}
                                            icon="success_outline"
                                        >
                                            <span className="hidden sm:inline">Mark as closed</span>
                                        </Button>
                                    </div>
                                );
                            }

                            return (
                                <div className="order-2 md:order-1">
                                    <Button
                                        title="Mark in progress"
                                        onClick={handleProgressActionClick}
                                        loading={isProcessing}
                                        disabled={isProcessing}
                                        icon="chevron_right"
                                        variant="primaryYellow"
                                    >
                                        <span className="hidden sm:inline">mark in progress</span>
                                    </Button>
                                </div>
                            );
                        })()}
                        <div className="order-1 md:order-2">
                            <ButtonNoStyles onClick={onClose}>
                                <Icon icon="cross" />
                            </ButtonNoStyles>
                        </div>
                    </div>
                </div>
                <div className={styles.content}>
                    <ActionViewDetails actionId={actionId} />
                    <ActionViewCommentsContainer actionId={actionId} comments={comments} onCommentAdded={commentAdded} />
                </div>
            </Loading>
            {showCommentWarning ? (
                <Modal show={showCommentWarning} onClose={closeModal} full>
                    <div className="max-w-[25rem]">
                        <Alert
                            type="warning"
                            title="Please add a closing comment"
                            description="A comment is required to proceed. Please log the reason why you are closing this action."
                            confirmLabel="Add a comment"
                            onConfirm={closeModal}
                        />
                    </div>
                </Modal>
            ) : null}
        </Card>
    );
};

export default ActionView;
